import { ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import type { SupplyItemRow, SupplyRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** `wl_supply_order.supply_status`（docs/03 §2.8）。 */
const SUPPLY_STATUS: Record<string, { text: string; color: string }> = {
  NEW: { text: '待受理', color: 'processing' },
  ACCEPTED: { text: '已受理', color: 'cyan' },
  PICKING: { text: '拣货中', color: 'blue' },
  PACKED: { text: '已打包', color: 'geekblue' },
  SHIPPED: { text: '已出库', color: 'green' },
  DELIVERING: { text: '配送中', color: 'green' },
  SIGNED: { text: '已签收', color: 'success' },
  CANCELED: { text: '已取消', color: 'default' }
};

const LINE_STATUS: Record<string, string> = {
  NEW: '待出库',
  PICKING: '拣货中',
  PICKED: '已拣货',
  DONE: '已完成',
  CANCELED: '已取消'
};

const TRIGGER_TYPE: Record<string, string> = {
  STOCK_OUT: '院内缺货',
  FORCE_OUTER: '强制外仓',
  EMERGENCY: '应急'
};

/**
 * 与后端 `SupplyOrder.OutAsync` 的门禁同口径：只有已出库/已取消的指令不可再出库，
 * 其余状态（含后端后续新增的中间态）都应允许，避免「前端不给按钮、后端其实能出库」。
 */
const OUT_BLOCKED = ['SHIPPED', 'DELIVERING', 'SIGNED', 'CANCELED'];
const canOut = (status: string): boolean => !OUT_BLOCKED.includes(status);

type SupplyFilter = 'ALL' | 'NEW' | 'ACCEPTED' | 'PICKING' | 'PACKED' | 'SHIPPED' | 'CANCELED';

/**
 * 外部仓 WMS：外仓代履供货指令（`GY` 单）。
 * 出库确认在同一事务内生成院内 `RK`（OUTER_SUPPLY）+ `CK`（OUTER_PASS）配对双单与双流水，
 * 净库存为 0、成本转移到需求科室（docs/03 §E1/E5/E10）。
 */
export default function WmsSupply() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<SupplyFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const list = useInfiniteQuery({
    queryKey: ['ops', 'supply', 'list', filter],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.supply.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        supplyStatus: filter === 'ALL' ? undefined : filter
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['ops', 'supply', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.supply.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const out = useMutation({
    mutationFn: (id: string) => hq.supply.out(id),
    onSuccess: async (result) => {
      modal.success({
        title: '外仓出库完成',
        content: (
          <Space direction="vertical" size={4}>
            <Typography.Text>
              供货指令 {result.supplyBizNo} 已出库（{formatQty(result.totalQty)}），状态置为
              <Tag color="green" style={{ marginInline: 6 }}>
                SHIPPED
              </Tag>
            </Typography.Text>
            <Typography.Text>院内入库单：{result.inboundBizNo}</Typography.Text>
            <Typography.Text>院内出库单：{result.outboundBizNo}</Typography.Text>
            <Typography.Text type="secondary">
              收货仓 {result.warehouseName ?? result.warehouseId}
              {result.isOvertime ? '；本次已超出 SLA 时限' : ''}
            </Typography.Text>
          </Space>
        )
      });
      await queryClient.invalidateQueries({ queryKey: ['ops', 'supply'] });
      await detail.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '出库确认失败，请稍后重试');
    }
  });

  const confirmOut = (row: SupplyRow) => {
    modal.confirm({
      title: `确认外仓出库：${row.bizNo}`,
      content: (
        <Space direction="vertical" size={4}>
          <Typography.Text>
            将按 {formatQty(row.totalQty)} / {formatAmount(row.totalAmount)} 全额出库。
          </Typography.Text>
          <Typography.Text type="secondary">
            出库后自动生成院内入库单（OUTER_SUPPLY）与直通出库单（OUTER_PASS），两单互指、库存先入后出。
          </Typography.Text>
        </Space>
      ),
      okText: '确认出库',
      onOk: () => out.mutateAsync(row.id)
    });
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<SupplyRow> = [
    {
      title: '指令号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    {
      title: '需求科室',
      dataIndex: 'demandDeptName',
      width: 150,
      render: (value: string | null, row) => value || row.demandDeptId
    },
    {
      title: '触发',
      dataIndex: 'triggerType',
      width: 110,
      render: (value: string) => (
        <Tag color={value === 'EMERGENCY' ? 'red' : value === 'FORCE_OUTER' ? 'orange' : 'default'}>
          {TRIGGER_TYPE[value] ?? value}
        </Tag>
      )
    },
    { title: '来源申领单', dataIndex: 'sourceBizNos', ellipsis: { showTitle: false }, render: ellipsisCell('sourceBizNos') },
    {
      title: '状态',
      dataIndex: 'supplyStatus',
      width: 110,
      render: (value: string) => {
        const meta = SUPPLY_STATUS[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '总数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '总金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: 'SLA 截止',
      dataIndex: 'slaDeadline',
      width: 160,
      render: (value: string | null, row) =>
        value ? (
          <Space size={4}>
            <span>{formatDisplayTime(value)}</span>
            {row.isOvertime ? <Tag color="red">超时</Tag> : null}
          </Space>
        ) : (
          '-'
        )
    },
    { title: '出库时间', dataIndex: 'outAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => setCurrentId(row.id)}>
            详情
          </Button>
          {canOut(row.supplyStatus) ? (
            <Button
              type="link"
              icon={<ExportOutlined />}
              loading={out.isPending && out.variables === row.id}
              onClick={() => confirmOut(row)}
            >
              出库确认
            </Button>
          ) : null}
        </Space>
      )
    }
  ];

  const itemColumns: ColumnsType<SupplyItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '物资编码', dataIndex: 'materialCode', width: 140, render: (value: string | null) => value || '-' },
    { title: '物资', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 130, render: (value: string | null) => value || '-' },
    {
      title: '计划量',
      dataIndex: 'planQty',
      width: 100,
      align: 'right',
      render: (value: number, row) => `${formatQty(value)} ${row.unitName ?? ''}`
    },
    { title: '已出库', dataIndex: 'outQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '批号', dataIndex: 'batchNo', width: 130, render: (value: string | null) => value || '-' },
    { title: '单价', dataIndex: 'price', width: 110, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '行状态',
      dataIndex: 'lineStatus',
      width: 100,
      render: (value: string | null) => (value ? (LINE_STATUS[value] ?? value) : '-')
    }
  ];

  const order = detail.data?.order;

  return (
    <Card
      className="page-card"
      title="外部仓 WMS"
      extra={
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
          刷新
        </Button>
      }
    >
      <Segmented<SupplyFilter>
        value={filter}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '待受理', value: 'NEW' },
          { label: '已受理', value: 'ACCEPTED' },
          { label: '拣货中', value: 'PICKING' },
          { label: '已打包', value: 'PACKED' },
          { label: '已出库', value: 'SHIPPED' },
          { label: '已取消', value: 'CANCELED' }
        ]}
        onChange={(value) => setFilter(value)}
        style={{ marginBottom: 12 }}
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="供货指令加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<SupplyRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无外仓供货指令（由申领判源生成）' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={order?.bizNo ?? '外仓供货指令'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={960}
        extra={
          order && canOut(order.supplyStatus) ? (
            <Button type="primary" icon={<ExportOutlined />} loading={out.isPending} onClick={() => confirmOut(order)}>
              出库确认
            </Button>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : order ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                <Tag color={SUPPLY_STATUS[order.supplyStatus]?.color ?? 'default'}>
                  {SUPPLY_STATUS[order.supplyStatus]?.text ?? order.supplyStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="触发类型">{TRIGGER_TYPE[order.triggerType] ?? order.triggerType}</Descriptions.Item>
              <Descriptions.Item label="需求科室">{order.demandDeptName || order.demandDeptId}</Descriptions.Item>
              <Descriptions.Item label="外仓">
                {order.outerWarehouseName ?? `仓库 ${order.outerWarehouseId}`}
              </Descriptions.Item>
              <Descriptions.Item label="来源申领单" span={2}>
                {order.sourceBizNos || order.sourceApplyId}
              </Descriptions.Item>
              <Descriptions.Item label="总数量">{formatQty(order.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="总金额">{formatAmount(order.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="SLA 截止">
                <Space size={4}>
                  <span>{formatDisplayTime(order.slaDeadline)}</span>
                  {order.isOvertime ? <Tag color="red">超时</Tag> : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="通知时间">{formatDisplayTime(order.notifyAt)}</Descriptions.Item>
              <Descriptions.Item label="出库时间">{formatDisplayTime(order.outAt)}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatDisplayTime(order.createAt)}</Descriptions.Item>
            </Descriptions>

            <Table<SupplyItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data?.items ?? []}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
